import useRedirectToPage from '../../../../hooks/useRedirectToPage.jsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonContainer,
  StyledPopover,
} from '../../form/FormElements.jsx';
import { routes } from '../../../../utils/helpers/ApiClient.jsx';
import dayjs from 'dayjs';
import { DATETIME_FORMAT_TIME } from '../../../../utils/helpers/CommonHelper.jsx';
import { RESERVATION_STATUS } from '../../../../utils/constants/enums.js';
import { PlusOutlined } from '@ant-design/icons';

const ReservationTableItem = ({
  reservationData,
  popoverTarget,
  itemId,
  user,
  isCms = false,
  isDisabled = false,
  rowIndex,
  colIndex,
  tabIndex = -1,
  onCellFocus,
  onCellKeyDown,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const redirectToPage = useRedirectToPage();
  const contentRef = useRef(null);
  const addButtonRef = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Find last reservation for the day
  let lastReservationTime = null;
  if (reservationData && reservationData.length > 0) {
    const lastReservation = [...reservationData].sort((a, b) =>
      dayjs(a.date_to).diff(dayjs(b.date_to)),
    )[reservationData.length - 1];
    lastReservationTime = dayjs(lastReservation.date_to).format('HH:mm');
  }

  useEffect(() => {
    if (!isPopoverOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      const focusTarget =
        addButtonRef.current ??
        contentRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? contentRef.current;

      focusTarget?.focus?.();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [isPopoverOpen]);

  const handleOpenChange = (open) => {
    setIsPopoverOpen(open);
  };

  const handleCreateReservation = () => {
    let url = `${routes.cmsReservationsCreate}?room_id=${popoverTarget.room}&day=${popoverTarget.day}&origin=${isCms ? 'cms' : 'public'}`;
    if (lastReservationTime) {
      url += `&last_time=${lastReservationTime}`;
    }
    redirectToPage(url);
  };

  const handlePopupKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsPopoverOpen(false);
      document.getElementById(itemId)?.focus();
      return;
    }

    if (event.key === 'Tab') {
      setIsPopoverOpen(false);
      event.preventDefault();
      window.setTimeout(() => {
        document.getElementById(itemId)?.focus();
      }, 0);
    }
  };

  const addReservationButton = (
    <ButtonContainer>
      <button
        ref={addButtonRef}
        type="button"
        className="resover-mainbutton"
        title={t('Pridať rezerváciu')}
        aria-label={t('Pridať rezerváciu')}
        onClick={handleCreateReservation}
      >
        <PlusOutlined aria-hidden="true" />
      </button>
    </ButtonContainer>
  );

  const isTodayOrFuture = !dayjs(popoverTarget.day).isBefore(dayjs(), 'day');

  const showAddButton =
    isTodayOrFuture &&
    reservationData &&
    reservationData.length > 0 &&
    reservationData.every(
      (reservation) =>
        dayjs(reservation.date_from).format('HH:mm') !== '00:00' &&
        dayjs(reservation.date_to).format('HH:mm') !== '23:59',
    );

  const content = (
    <div
      ref={contentRef}
      style={{ padding: '10px' }}
      tabIndex={-1}
      onKeyDown={handlePopupKeyDown}
    >
      {reservationData &&
        reservationData.map((reservation) => (
          <span key={reservation.id}>
            {dayjs(reservation.date_from).format(DATETIME_FORMAT_TIME)}
            {' - '}
            {dayjs(reservation.date_to).format(DATETIME_FORMAT_TIME)}
            <strong> {reservation.full_name}</strong>
            &nbsp;(<em>{reservation.reason}</em>)
            <br />
          </span>
        ))}

      {reservationData && reservationData.length === 0 && (
        <span>{t('V tento deň nie sú žiadne rezervácie.')}</span>
      )}

      {/* Show button only once per day if conditions are met */}
      {((reservationData &&
        reservationData.length === 0 &&
        isTodayOrFuture) ||
        showAddButton) &&
        addReservationButton}
    </div>
  );

  return (
    <>
      <StyledPopover
        trigger="click"
        placement="right"
        content={content}
        open={isPopoverOpen}
        onOpenChange={handleOpenChange}
        target={
          popoverTarget !== 'html'
            ? 'room-' + popoverTarget.room + 'day-' + popoverTarget.day
            : 'html'
        }
      >
        <td
          id={itemId}
          role="gridcell"
          className={`resdate-item${isDisabled ? ' disabled' : ''}`}
          data-row-index={rowIndex}
          data-col-index={colIndex}
          tabIndex={tabIndex}
          aria-label={ariaLabel}
          aria-disabled={isDisabled}
          aria-keyshortcuts="Enter Space"
          onFocus={() => {
            if (onCellFocus) {
              onCellFocus(rowIndex, colIndex);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.currentTarget.click();
              return;
            }

            if (onCellKeyDown) {
              onCellKeyDown(event, rowIndex, colIndex);
            }
          }}
        >
          {' '}
          {/* add class for clarity */}
          {reservationData &&
            reservationData.map((reservation) => (
              <span
                key={reservation.id}
                style={{
                  width:
                    dayjs(reservation.date_to).diff(
                      dayjs(reservation.date_from),
                    ) /
                      576000 +
                    '%',
                  backgroundColor: isDisabled
                    ? 'darkgrey'
                    : reservationColor(reservation, user, popoverTarget.day),
                }}
              >
                &#8203;
              </span>
            ))}
        </td>
      </StyledPopover>
    </>
  );
};

function reservationColor(reservation, user, day) {
  // Darkgrey only if the cell's day is fully in the past
  if (day && dayjs(day).endOf('day').isBefore(dayjs())) {
    return 'darkgrey';
  } else if (!user) {
    if (reservation.state === RESERVATION_STATUS.WAITING.STATE) {
      return '#C2D6F1';
    } else {
      return '#6B839F';
    }
  } else if (user && reservation.created_for_id === user.id) {
    if (reservation.state === RESERVATION_STATUS.WAITING.STATE) {
      return '#f5e0b5';
    } else {
      return '#FFB81C';
    }
  } else {
    if (reservation.state === RESERVATION_STATUS.WAITING.STATE) {
      return '#C2D6F1';
    } else {
      return '#6B839F';
    }
  }
}

export default ReservationTableItem;
